import React from 'react';
import './AdminElectionPage.scss';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/store';
import {
    asyncFetchAdminElection,
    asyncFetchAdminElections,
} from 'store/reducers/elections/thunks';
import { facultyGradientByName } from 'theme/faculties';
import getApiClient from 'js/apiClient';
import { IAdminElection } from 'store/reducers/elections/types';
import ElectionSummary from './ElectionSummary';

const POLL_INTERVAL = 5000;
const ACTIVITY_CLASS = 'admin-stats__section admin-stats__section--activity';
const electionDay = (date: string): string =>
    new Date(date).toLocaleDateString('sv-SE', { timeZone: 'Europe/Moscow' });

function useNow(): number {
    const [now, setNow] = React.useState(Date.now());
    React.useEffect(() => {
        const timer = window.setInterval(() => setNow(Date.now()), 250);
        return () => window.clearInterval(timer);
    }, []);
    return now;
}

function RotationTimeline({
    elections,
    currentId,
    progress,
    onSelect,
}: {
    elections: IAdminElection[];
    currentId: number;
    progress: number;
    onSelect: (id: number) => void;
}): React.ReactElement {
    return (
        <div
            className="admin-stats__rotation"
            role="list"
            aria-label="Очередь выборов"
        >
            {elections.map(election => {
                const current = election.id === currentId;
                const fill = current ? progress : 0;
                return (
                    <div
                        className={`admin-stats__rotation-item${
                            current
                                ? ' admin-stats__rotation-item--current'
                                : ''
                        }`}
                        role="listitem"
                        key={election.id}
                    >
                        <button
                            className="admin-stats__rotation-button"
                            type="button"
                            aria-current={current ? 'step' : undefined}
                            onClick={() => onSelect(election.id)}
                        >
                            <span>
                                {election.faculty?.name ||
                                    election.hostel?.name ||
                                    'СПбГУТ'}
                            </span>
                            <span
                                className="admin-stats__rotation-track"
                                aria-hidden="true"
                            >
                                <i style={{ width: `${fill}%` }} />
                            </span>
                        </button>
                    </div>
                );
            })}
        </div>
    );
}

function formatRemaining(ms: number): string {
    const total = Math.floor(ms / 1000);
    const days = Math.floor(total / 86400);
    const hours = Math.floor((total % 86400) / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;
    const clock = [hours, minutes, seconds]
        .map(value => String(value).padStart(2, '0'))
        .join(':');
    return days ? `${days} дн. ${clock}` : clock;
}

interface VoteActivity {
    electionId: number;
    window_start: string;
    window_end: string;
    bucket_minutes: number;
    votes_per_interval: number[];
}

function ActivityChart({
    activity,
    trimTrailingEmpty,
}: {
    activity: VoteActivity | undefined;
    trimTrailingEmpty: boolean;
}): React.ReactElement {
    if (!activity)
        return (
            <p className="admin-stats__empty">
                Загружаем динамику голосования…
            </p>
        );
    const values = [...activity.votes_per_interval];
    if (trimTrailingEmpty)
        while (values.length > 1 && values[values.length - 1] === 0)
            values.pop();
    const max = Math.max(1, ...values);
    const start = Date.parse(activity.window_start);
    const end = Date.parse(activity.window_end);
    const columns = values.map((count, index) => ({
        count,
        time: Math.min(
            end,
            start + (index + 1) * activity.bucket_minutes * 60000
        ),
    }));
    const gridColumns = `repeat(${columns.length}, minmax(0, 1fr))`;
    const dense = columns.length > 12;
    const labelEvery = columns.length > 48 ? 8 : 4;
    return (
        <div
            className={`admin-stats__activity-chart${
                dense ? ' admin-stats__activity-chart--dense' : ''
            }`}
            style={{ gridTemplateColumns: gridColumns }}
        >
            {columns.map(({ count, time }, index) => {
                const label = new Date(time).toLocaleTimeString('ru-RU', {
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZone: 'Europe/Moscow',
                });
                const showLabel =
                    !dense ||
                    (index + 1) % labelEvery === 0 ||
                    index === columns.length - 1;
                return (
                    <div
                        className="admin-stats__activity-column"
                        key={time}
                        title={`${label} · ${count} голосов`}
                    >
                        {!dense && <strong>{count}</strong>}
                        <div className="admin-stats__activity-track">
                            <span
                                style={{ height: `${(count / max) * 100}%` }}
                            />
                        </div>
                        <time
                            dateTime={new Date(time).toISOString()}
                            aria-hidden={!showLabel}
                        >
                            {showLabel ? label : '\u00a0'}
                        </time>
                    </div>
                );
            })}
        </div>
    );
}

interface RankRow {
    name: string;
    votes_count: number;
}

function Ranking({
    rows,
    empty,
}: {
    rows: RankRow[];
    empty: string;
}): React.ReactElement {
    if (!rows.length) return <p className="admin-stats__empty">{empty}</p>;
    const max = Math.max(1, ...rows.map(row => row.votes_count));
    return (
        <ol className="admin-stats__ranking">
            {rows.map(row => (
                <li key={row.name}>
                    <div className="admin-stats__rank-line">
                        <span>{row.name}</span>
                        <strong>{row.votes_count}</strong>
                    </div>
                    <div className="admin-stats__bar" aria-hidden="true">
                        <span
                            style={{
                                width: `${(row.votes_count / max) * 100}%`,
                            }}
                        />
                    </div>
                </li>
            ))}
        </ol>
    );
}

function AdminElectionPage(): React.ReactElement {
    const dispatch = useAppDispatch();
    const now = useNow();
    const { electionId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const { adminElections, adminElection } = useAppSelector(
        state => state.electionsStore
    );
    const [rotationStartedAt, setRotationStartedAt] = React.useState(
        Date.now()
    );
    const [rotationSelection, setRotationSelection] = React.useState<{
        id: number;
    }>();
    const adminElectionsRef = React.useRef(adminElections);
    adminElectionsRef.current = adminElections;
    const adminElectionRef = React.useRef(adminElection);
    adminElectionRef.current = adminElection;
    const [activity, setActivity] = React.useState<VoteActivity>();
    React.useLayoutEffect(() => {
        document.body.classList.add('admin-stats-view');
        return () => document.body.classList.remove('admin-stats-view');
    }, []);
    const anchor =
        adminElections.find(election => election.id === Number(electionId)) ||
        adminElection;
    const day = anchor ? electionDay(anchor.date_end) : undefined;
    const rotatingElections = adminElections.filter(
        election =>
            election.is_active &&
            Date.parse(election.date_start) <= now &&
            (!day || electionDay(election.date_end) === day)
    );
    const rotationIds = rotatingElections
        .map(election => election.id)
        .join(',');

    React.useEffect(() => {
        if (!rotationIds) {
            dispatch(asyncFetchAdminElections());
            return undefined;
        }
        const ids = rotationIds.split(',').map(Number);
        let index = Math.max(0, ids.indexOf(rotationSelection?.id || ids[0]));
        const load = (): void => {
            const tick = Date.now();
            const closingElection = adminElectionsRef.current.find(election => {
                const left = Date.parse(election.date_end) - tick;
                return ids.includes(election.id) && left > 0 && left <= 15000;
            });
            const { current } = adminElectionRef;
            const currentLeft = current
                ? Date.parse(current.date_end) - tick
                : 0;
            const currentClosing =
                current?.is_active && currentLeft > 0 && currentLeft <= 15000;
            let targetId = ids[index];
            if (closingElection) targetId = closingElection.id;
            if (closingElection && currentClosing && current)
                targetId = current.id;
            if (targetId !== current?.id) setRotationStartedAt(tick);
            dispatch(asyncFetchAdminElection(targetId));
            dispatch(asyncFetchAdminElections());
            if (!closingElection) index = (index + 1) % ids.length;
        };
        load();
        const timer = window.setInterval(load, POLL_INTERVAL);
        return () => window.clearInterval(timer);
    }, [rotationIds, rotationSelection, dispatch, electionId]);

    React.useEffect(() => {
        if (!rotationIds && !adminElection)
            dispatch(asyncFetchAdminElection(Number(electionId)));
    }, [rotationIds, adminElection, dispatch, electionId]);

    const activityId = adminElection?.id;
    React.useEffect(() => {
        if (!activityId) return undefined;
        const id = activityId;
        const load = (): void => {
            getApiClient('admin')
                .get(`admin/elections/${id}/activity/`)
                .then(response => {
                    setActivity({ ...response.data, electionId: id });
                })
                .catch(() => setActivity(undefined));
        };
        load();
        const timer = window.setInterval(load, POLL_INTERVAL);
        return () => window.clearInterval(timer);
    }, [activityId]);

    const remaining = adminElection?.date_end
        ? Math.max(0, Date.parse(adminElection.date_end) - now)
        : 0;
    const ended = Boolean(
        adminElection && Date.parse(adminElection.date_end) <= now
    );

    React.useEffect(() => {
        if (ended) dispatch(asyncFetchAdminElections());
    }, [dispatch, ended]);

    if (!adminElection) {
        return (
            <div className="admin-stats" role="status">
                Загружаем статистику…
            </div>
        );
    }

    if (ended && searchParams.has('summary')) {
        const summaryDay = electionDay(adminElection.date_end);
        const finished = adminElections.filter(
            election =>
                election.is_active &&
                Date.parse(election.date_end) <= now &&
                electionDay(election.date_end) === summaryDay
        );
        if (!finished.length) {
            return (
                <div className="admin-stats" role="status">
                    Загружаем общие результаты…
                </div>
            );
        }
        return (
            <ElectionSummary
                elections={finished}
                onBack={() => setSearchParams({})}
            />
        );
    }

    const gradient = adminElection.faculty
        ? facultyGradientByName(
              adminElection.faculty.name,
              adminElection.faculty.short_name
          )
        : 'var(--rd-grad-studsovet)';
    const turnout = new Intl.NumberFormat('ru-RU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(adminElection.turnout * 100);
    const showEmployees = !adminElection.faculty && !adminElection.hostel;
    const totalVotes =
        adminElection.students_votes_count +
        adminElection.employees_votes_count;
    const faculties = [...adminElection.faculties_map].sort(
        (a, b) => b.votes_count - a.votes_count
    );
    const courses = Object.entries(adminElection.courses_map)
        .map(([name, votes_count]) => ({ name: `${name} курс`, votes_count }))
        .sort((a, b) => b.votes_count - a.votes_count)
        .slice(0, 5);
    const groups = [...adminElection.groups_map]
        .sort((a, b) => b.votes_count - a.votes_count)
        .slice(0, 6);
    const showFaculties =
        !adminElection.faculty && !adminElection.hostel && faculties.length > 1;
    const start = Date.parse(adminElection.date_start);
    const active = adminElection.is_active && now >= start && remaining > 0;
    const closing = active && remaining <= 15000;
    const showLiveActivity =
        active && typeof adminElection.votes_last_15_minutes === 'number';
    let status = 'Голосование завершено';
    if (now < start) status = 'Голосование ещё не началось';
    else if (remaining > 0 && !adminElection.is_active)
        status = 'Голосование недоступно';
    else if (active) status = 'Идёт голосование';
    const title = adminElection.name.replace(
        /студсовета(?: факультета)?/i,
        showEmployees
            ? 'Студенческого совета'
            : 'Студенческого совета факультета'
    );
    const currentActivity =
        activity?.electionId === adminElection.id ? activity : undefined;
    const showRotationTimeline = !closing && rotatingElections.length > 1;
    const rotationProgress = Math.min(
        100,
        Math.max(0, ((now - rotationStartedAt) / POLL_INTERVAL) * 100)
    );

    return (
        <div
            className="admin-stats"
            style={{ '--admin-stats-accent': gradient } as React.CSSProperties}
        >
            <Link className="admin-stats__back" to="/admin">
                ← Все выборы
            </Link>
            <header
                className={`admin-stats__hero${
                    showRotationTimeline ? ' admin-stats__hero--rotating' : ''
                }`}
                style={{ background: gradient }}
            >
                {showRotationTimeline && (
                    <RotationTimeline
                        elections={rotatingElections}
                        currentId={adminElection.id}
                        progress={rotationProgress}
                        onSelect={id => {
                            setRotationStartedAt(Date.now());
                            setRotationSelection({ id });
                        }}
                    />
                )}
                <div className="admin-stats__hero-main">
                    <h1>{title}</h1>
                    {!active && (
                        <p className="admin-stats__status">
                            <span aria-hidden="true" />
                            {status}
                        </p>
                    )}
                </div>
                {active && !closing && (
                    <div className="admin-stats__countdown">
                        <span>До завершения голосования</span>
                        <strong>{formatRemaining(remaining)}</strong>
                    </div>
                )}
                {ended && (
                    <button
                        className="admin-stats__summary-link"
                        type="button"
                        onClick={() => setSearchParams({ summary: '1' })}
                    >
                        Общие результаты →
                    </button>
                )}
            </header>

            <section
                className={`admin-stats__totals${
                    showLiveActivity ? ' admin-stats__totals--live' : ''
                }`}
                aria-label="Число голосов"
            >
                <div>
                    <span>Явка студентов</span>
                    <strong>{turnout}%</strong>
                </div>
                <div>
                    <span>Всего голосов</span>
                    <strong>{totalVotes}</strong>
                </div>
                <div>
                    <span>Голоса студентов</span>
                    <strong>{adminElection.students_votes_count}</strong>
                </div>
                {showEmployees && (
                    <div>
                        <span>Голоса сотрудников</span>
                        <strong>{adminElection.employees_votes_count}</strong>
                    </div>
                )}
                {showLiveActivity && (
                    <div>
                        <span>Голосов за 15 минут</span>
                        <strong>{adminElection.votes_last_15_minutes}</strong>
                    </div>
                )}
            </section>

            <div className="admin-stats__analytics">
                <section className={ACTIVITY_CLASS}>
                    <h2>Темп голосования</h2>
                    <p>
                        С начала голосования · интервал{' '}
                        {currentActivity?.bucket_minutes || '…'} мин
                    </p>
                    <ActivityChart
                        activity={currentActivity}
                        trimTrailingEmpty={ended}
                    />
                </section>
                <div
                    className={`admin-stats__details${
                        showFaculties ? '' : ' admin-stats__details--pair'
                    }`}
                >
                    {showFaculties && (
                        <section className="admin-stats__section">
                            <h2>Голоса по подразделениям</h2>
                            <Ranking
                                rows={faculties}
                                empty="Пока нет голосов по подразделениям"
                            />
                        </section>
                    )}
                    <section className="admin-stats__section">
                        <h2>Голоса по курсам</h2>
                        <Ranking
                            rows={courses}
                            empty="Пока нет голосов по курсам"
                        />
                    </section>
                    <section className="admin-stats__section">
                        <h2>Голоса по группам</h2>
                        <Ranking
                            rows={groups}
                            empty="Пока нет голосов по группам"
                        />
                    </section>
                </div>
            </div>
            {closing && (
                <div
                    className="admin-stats__final-countdown"
                    style={{ background: gradient }}
                    role="status"
                    aria-live="off"
                >
                    <span>До завершения голосования</span>
                    <strong
                        style={{
                            transform: `scale(${
                                1 + (15000 - remaining) / 30000
                            })`,
                        }}
                    >
                        {Math.ceil(remaining / 1000)}
                    </strong>
                </div>
            )}
        </div>
    );
}

export default AdminElectionPage;
